import React, { useCallback, useMemo } from 'react';
import { first } from 'lodash';

import ICO_CALENDAR from 'assets/images/menu/CalendarIcon.svg';
import ICO_TIMING from 'assets/images/online-booking/Timings-purple.svg';
import determineHoursLaterForNcob from 'bl/ncob/determineHoursLaterForNcob.bl';
import CheckboxInput from 'components/form/CheckboxInput.component';
import DropdownInput from 'components/form/DropdownInput.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import WidgetTiming from 'components/widget/WidgetTiming.component';
import NCOB_BOOKING_TIMING_HOURS from 'constants/settings/ncobBookingTimingHours.const';
import useAfn from 'hooks/useAfn.hook';
import { useOnlineBookingPermissions } from 'hooks/useOnlineBookingPermissions.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { formatDayToYearFullWeekdaySuffix } from 'utils/date/format.util';
import { timeAddDays } from 'utils/date/manipulate.util';
import { nowAsDate } from 'utils/date/now.util';
import nullBind from 'utils/functions/nullBind.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import WidgetAvailabilitySettings from 'settings/online-booking/pages/booking-timings/WidgetAvailabilitySettings.component';
import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionLabel from 'settings/online-booking/pages/SectionLabel.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import SectionTitle from 'settings/online-booking/pages/SectionTitle.component';

import CN from './BookingTimingsPanel.module.scss';

const L = logger('BookingTimingsPanel');

// just to stop the ESLint/Prettier auto-format from removing ={true}
const T = true;
const F = false;

const fromEvToValue = nullBind(getAnyOrVoid, 'raw.value');
const fromEvToBool = nullBind(getAnyOrVoid, 'bool');

const byDescription = ($) => (candidate) => asObject(candidate).description === $;
const byValue = ($) => (candidate) => asObject(candidate).value === $;

const fromDaysToWeeks = (value) => {
  if (!Number.isSafeInteger(value) || value < 0) return 0;
  return Math.floor(value / 7);
};

const fromWeekToOptions = () =>
  Array.from({ length: 26 }, (_, i) => i + 1).map((week) => ({
    value: week * 7,
    description: fromAnyToTrimmed(week),
  }));

const BookingTimingsPanel = () => {
  const { t } = useTranslation();
  const { value, isLoading, error, update } = useNcobSettings();
  const { checkNcobAvailabilityUntil, bookNcobWithSame, bookingTimings, closestBookingTime } = asObject(value);

  // GS online booking phase 3 — `can_edit_availability` enforcement. When the
  // user lacks the permission, availability edits are blocked with a toast and
  // the underlying NCOB settings are left untouched (read-only).
  const { blockEditAvailability } = useOnlineBookingPermissions();

  const untilWeeks = fromDaysToWeeks(checkNcobAvailabilityUntil);
  const options = useMemo(() => fromWeekToOptions(), []);
  const selectedWeek = options.find(byValue(untilWeeks * 7)) || first(options);

  const untilFromNow = timeAddDays(checkNcobAvailabilityUntil, nowAsDate());

  const handleWeeks = useCallback(
    (ev) => {
      if (blockEditAvailability()) return;
      update({ data: { checkNcobAvailabilityUntil: fromEvToValue(ev) } });
    },
    [blockEditAvailability, update],
  );

  const handleAvailability = useCallback(
    (ev) => {
      if (blockEditAvailability()) return;
      update({ data: { bookingTimings: fromEvToValue(ev) } });
    },
    [blockEditAvailability, update],
  );

  const handleSameStylist = useCallback(
    (ev) => {
      if (blockEditAvailability()) return;
      update({ data: { bookNcobWithSame: fromEvToBool(ev) } });
    },
    [blockEditAvailability, update],
  );

  const [, handleSaveHours] = useAfn(
    async ({ raw }) => {
      if (raw === null) return { value: closestBookingTime };
      if (blockEditAvailability()) return { value: closestBookingTime };

      const offsetValue = NCOB_BOOKING_TIMING_HOURS.find(byDescription(raw.description));
      if (!offsetValue) return { value: closestBookingTime };

      const { value } = await update({ data: { closestBookingTime: offsetValue.value } });
      return value;
    },
    [blockEditAvailability, closestBookingTime, update],
  );

  const [, handleSaveTiming] = useAfn(
    async ({ raw }) => {
      if (raw === null) return { value: closestBookingTime };
      if (blockEditAvailability()) return { value: closestBookingTime };

      const { hour } = raw;

      const { ok, value } = await update({ data: { closestBookingTime: hour } });
      return ok ? value : { value: hour };
    },
    [blockEditAvailability, closestBookingTime, update],
  );

  const hoursDescription = useMemo(() => {
    const hoursValue = determineHoursLaterForNcob(closestBookingTime);
    const hoursUnit = hoursValue === 1 ? t('common.hours.hour') : t('common.hours.hours');

    return `${hoursValue} ${hoursUnit}`;
  }, [closestBookingTime, t]);

  if (error) L.warn('()', { isLoading, error, value });

  return (
    <div data-bem="BookingTimingsPanel">
      <Foldout tag="BookingTimingsPanel__timings" src={ICO_TIMING} title={t('onlineBooking.bookingTimings.timing.title1')} isOpen>
        {isLoading && <OverlaySpinner />}

        <SectionSeparator />

        <SectionTitle value={t('onlineBooking.bookingTimings.timing.title1')}>{t('onlineBooking.bookingTimings.timing.description1Part1')}</SectionTitle>

        <SectionContent>
          <SectionLabel>{t('onlineBooking.bookingTimings.timing.description1Part2')}</SectionLabel>
          <div className={CN.horizontal}>
            <span className={CN.dropdown}>
              <DropdownInput onChange={handleWeeks} options={options} value={selectedWeek} />
            </span>
            <span className={CN.advance}>
              <Icon variant="calendar" />
              {t('onlineBooking.bookingTimings.timing.description1Part3')}
              <Space>{formatDayToYearFullWeekdaySuffix(untilFromNow)}</Space>
            </span>
          </div>
        </SectionContent>

        <SectionSeparator />

        <SectionTitle value={t('onlineBooking.bookingTimings.timing.title2')}>{t('onlineBooking.bookingTimings.timing.description2')}</SectionTitle>

        <SectionContent>
          <SectionLabel> {t('onlineBooking.bookingTimings.timing.guide', { hours: hoursDescription })}</SectionLabel>
          <WidgetTiming onChangeTiming={handleSaveTiming} onChangeHours={handleSaveHours} value={closestBookingTime} offsetHours={NCOB_BOOKING_TIMING_HOURS} />
        </SectionContent>
      </Foldout>

      <Foldout tag="BookingTimingsPanel__availability" src={ICO_CALENDAR} title={t('onlineBooking.bookingTimings.availability.title1')} isOpen>
        <SectionTitle value={t('onlineBooking.bookingTimings.availability.title1')} />
        <SectionContent>
          <WidgetAvailabilitySettings onChange={handleAvailability} value={bookingTimings} />
        </SectionContent>
        <SectionSeparator />
        <SectionTitle value={t('onlineBooking.bookingTimings.availability.title2')}>{t('onlineBooking.bookingTimings.availability.description')}</SectionTitle>
        <SectionContent>
          <div className={CN.space}>
            <CheckboxInput variant="option" checked={bookNcobWithSame === T} value={T} onClick={handleSameStylist}>
              {t('onlineBooking.bookingTimings.availability.checkbox1Text')}
            </CheckboxInput>
          </div>
          <div className={CN.space}>
            <CheckboxInput variant="option" checked={bookNcobWithSame === F} value={F} onClick={handleSameStylist}>
              {t('onlineBooking.bookingTimings.availability.checkbox2Text')}
            </CheckboxInput>
          </div>
        </SectionContent>
      </Foldout>
    </div>
  );
};

export default BookingTimingsPanel;
